import { useState, useEffect, useCallback, useRef } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import PanelLayout from '@/components/PanelLayout';
import AssistantPanel from '@/components/AssistantPanel';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { API } from '@/utils/api';
import {
  Mail, Bell, Loader2, Check, Clock, Send,
  AlertTriangle, Zap, MessageCircle, BarChart3, UserX
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

/* ─── QUIET HOURS PICKER ─── */
function TimePicker({ value, onChange, testId }) {
  const hours = Array.from({ length: 24 }, (_, i) => i);
  return (
    <select
      value={value}
      onChange={e => onChange(parseInt(e.target.value))}
      data-testid={testId}
      className="bg-white border border-zinc-200 rounded-xl px-3 py-2.5 text-sm text-zinc-900 focus:border-[#F47A3B]/50 outline-none min-w-[90px]"
    >
      {hours.map(h => (
        <option key={h} value={h}>{String(h).padStart(2, '0')}:00</option>
      ))}
    </select>
  );
}

export default function EmailSettings() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [prefs, setPrefs] = useState(null);
  const [status, setStatus] = useState(null);
  const [logs, setLogs] = useState([]);
  const [sendingTest, setSendingTest] = useState(null);
  const saveTimer = useRef(null);

  useEffect(() => {
    Promise.all([
      fetch(`${API}/digest/preferences`, { headers }).then(r => r.json()),
      fetch(`${API}/digest/status`, { headers }).then(r => r.json()),
      fetch(`${API}/digest/logs?limit=5`, { headers }).then(r => r.json()),
    ]).then(([p, s, l]) => {
      setPrefs(p);
      setStatus(s);
      setLogs(l.logs || []);
    }).catch(console.error)
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line

  const save = useCallback((data) => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      setSaving(true);
      try {
        await fetch(`${API}/digest/preferences`, {
          method: 'PUT',
          headers: { ...headers, 'Content-Type': 'application/json' },
          body: JSON.stringify({ ...prefs, ...data }),
        });
      } catch (e) {
        console.error('Save failed:', e);
        toast.error('Nie udało się zapisać ustawień');
      } finally {
        setSaving(false);
      }
    }, 600);
  }, [prefs]); // eslint-disable-line

  const update = useCallback((field, value) => {
    setPrefs(prev => {
      const updated = { ...prev, [field]: value };
      save(updated);
      return updated;
    });
  }, [save]);

  const sendTest = async (type) => {
    setSendingTest(type);
    try {
      const res = await fetch(`${API}/digest/test-send?digest_type=${type}`, {
        method: 'POST',
        headers,
      });
      if (res.ok) {
        toast.success('Testowy email wysłany! Sprawdź skrzynkę.');
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Błąd wysyłania');
      }
    } catch {
      toast.error('Błąd połączenia');
    } finally {
      setSendingTest(null);
    }
  };

  if (loading) {
    return (
      <PanelLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 text-[#F47A3B] animate-spin" />
        </div>
      </PanelLayout>
    );
  }

  const TOGGLES = [
    {
      key: 'lead_alert_enabled',
      icon: MessageCircle,
      label: 'Nowe zapytania (Lead Alert)',
      desc: 'Otrzymasz powiadomienie, gdy klient czeka na odpowiedź',
      color: 'text-[#F47A3B]',
      bg: 'bg-[#F47A3B]/10',
      testType: 'lead_alert',
    },
    {
      key: 'daily_digest_enabled',
      icon: BarChart3,
      label: 'Codzienne podsumowanie (Daily Digest)',
      desc: 'Krótkie podsumowanie aktywności i zapytań',
      color: 'text-blue-500',
      bg: 'bg-blue-500/10',
      testType: 'daily_digest',
    },
    {
      key: 'inactive_reminder_enabled',
      icon: UserX,
      label: 'Przypomnienia o aktywności (Inactive Reminder)',
      desc: 'Powiadomimy Cię, gdy warto wrócić do panelu',
      color: 'text-amber-500',
      bg: 'bg-amber-500/10',
      testType: 'inactive_reminder',
    },
  ];

  return (
    <PanelLayout>
      <div className="max-w-[800px] mx-auto" data-testid="email-settings-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-[22px] font-bold text-zinc-900" data-testid="email-settings-title">Powiadomienia</h1>
            <p className="text-[14px] text-zinc-500 mt-0.5">Kontroluj kiedy i jakie powiadomienia e-mail otrzymujesz.</p>
          </div>
          <div className="flex items-center gap-2">
            {saving && <span className="flex items-center gap-1 text-xs text-[#F47A3B]"><Loader2 className="w-3 h-3 animate-spin" />Zapisuję...</span>}
            {!saving && prefs && <span className="flex items-center gap-1 text-xs text-emerald-500"><Check className="w-3 h-3" />Zapisano</span>}
          </div>
        </div>

        {/* Intro mikrocopy */}
        <div className="flex items-start gap-3 p-4 bg-[#F47A3B]/5 border border-[#F47A3B]/10 rounded-xl mb-6">
          <Zap className="w-4 h-4 text-[#F47A3B] mt-0.5 flex-shrink-0" />
          <p className="text-sm text-zinc-600">
            Wysyłamy tylko ważne powiadomienia, które pomagają zdobywać klientów i nie przegapić zapytań.
          </p>
        </div>

        {/* System status */}
        {status && (
          <div className={`flex items-center gap-3 p-4 rounded-xl border mb-6 ${
            status.enabled
              ? 'bg-emerald-50 border-emerald-200'
              : 'bg-amber-50 border-amber-200'
          }`} data-testid="email-system-status">
            {status.enabled ? (
              <>
                <div className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center">
                  <Check className="w-4 h-4 text-emerald-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-emerald-800">Powiadomienia aktywne</p>
                  <p className="text-xs text-emerald-600">Wysłano {status.total_sent || 0} maili, {status.sent_last_24h || 0} w ostatnich 24h (limit: 2/24h)</p>
                </div>
              </>
            ) : (
              <>
                <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center">
                  <AlertTriangle className="w-4 h-4 text-amber-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-amber-800">Tryb testowy</p>
                  <p className="text-xs text-amber-600">Emaile są wysyłane w trybie sandbox (czeka na weryfikację domeny)</p>
                </div>
              </>
            )}
          </div>
        )}

        {/* ═══ NOTIFICATION TOGGLES ═══ */}
        <div className="bg-white border border-zinc-200 rounded-2xl p-6 shadow-[0_2px_10px_rgba(0,0,0,0.04)] mb-6">
          <h2 className="text-[15px] font-bold text-zinc-900 mb-4">Powiadomienia e-mail</h2>
          <div className="space-y-4">
            {TOGGLES.map((t) => {
              const Icon = t.icon;
              const enabled = prefs?.[t.key] !== false;
              return (
                <div key={t.key} className="flex items-center justify-between" data-testid={`toggle-row-${t.key}`}>
                  <div className="flex items-center gap-3 flex-1 mr-4">
                    <div className={`w-10 h-10 rounded-xl ${t.bg} flex items-center justify-center flex-shrink-0`}>
                      <Icon className={`w-5 h-5 ${t.color}`} />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-zinc-900">{t.label}</p>
                      <p className="text-xs text-zinc-500">{t.desc}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => sendTest(t.testType)}
                      disabled={!!sendingTest}
                      className="text-[11px] text-zinc-400 hover:text-[#F47A3B] px-2 py-1 rounded-lg hover:bg-zinc-50 transition-colors"
                      data-testid={`test-send-${t.testType}`}
                    >
                      {sendingTest === t.testType ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3 inline mr-1" />}
                      {sendingTest === t.testType ? '' : 'Test'}
                    </button>
                    <Switch
                      checked={enabled}
                      onCheckedChange={(v) => update(t.key, v)}
                      data-testid={`toggle-${t.key}`}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* ═══ QUIET HOURS ═══ */}
        <div className="bg-white border border-zinc-200 rounded-2xl p-6 shadow-[0_2px_10px_rgba(0,0,0,0.04)] mb-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-zinc-100 flex items-center justify-center">
              <Clock className="w-5 h-5 text-zinc-500" />
            </div>
            <div>
              <h2 className="text-[15px] font-bold text-zinc-900">Godziny ciszy</h2>
              <p className="text-xs text-zinc-500">W tych godzinach nie wysyłamy powiadomień e-mail</p>
            </div>
          </div>
          <div className="flex items-center gap-3" data-testid="quiet-hours-section">
            <span className="text-sm text-zinc-600">od</span>
            <TimePicker
              value={prefs?.quiet_hours_start ?? 22}
              onChange={v => update('quiet_hours_start', v)}
              testId="quiet-hours-start"
            />
            <span className="text-sm text-zinc-600">do</span>
            <TimePicker
              value={prefs?.quiet_hours_end ?? 7}
              onChange={v => update('quiet_hours_end', v)}
              testId="quiet-hours-end"
            />
          </div>
        </div>

        {/* ═══ RECENT HISTORY ═══ */}
        <div className="bg-white border border-zinc-200 rounded-2xl p-6 shadow-[0_2px_10px_rgba(0,0,0,0.04)]">
          <h2 className="text-[15px] font-bold text-zinc-900 mb-4">Ostatnie wysyłki</h2>
          {logs.length > 0 ? (
            <div className="space-y-2">
              {logs.map((item, i) => (
                <div key={i} className="flex items-center justify-between p-3 bg-zinc-50 rounded-xl border border-zinc-100" data-testid={`log-${i}`}>
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                      item.status === 'sent' ? 'bg-emerald-100' : 'bg-red-100'
                    }`}>
                      {item.status === 'sent' ? (
                        <Check className="w-4 h-4 text-emerald-600" />
                      ) : (
                        <Mail className="w-4 h-4 text-red-500" />
                      )}
                    </div>
                    <div>
                      <p className="text-sm text-zinc-900">{item.subject || item.digest_type || 'Email'}</p>
                      <p className="text-[11px] text-zinc-400">
                        {item.sent_at ? new Date(item.sent_at).toLocaleDateString('pl-PL', {
                          day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit'
                        }) : '—'}
                      </p>
                    </div>
                  </div>
                  <span className={`text-[11px] px-2 py-1 rounded-full font-medium ${
                    item.status === 'sent'
                      ? 'bg-emerald-100 text-emerald-700'
                      : 'bg-red-100 text-red-600'
                  }`}>
                    {item.status === 'sent' ? 'Wysłany' : 'Błąd'}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <Mail className="w-10 h-10 mx-auto mb-2 text-zinc-200" />
              <p className="text-sm text-zinc-400">Brak wysłanych powiadomień</p>
            </div>
          )}
        </div>

        {/* Assistant */}
        <div className="mt-6">
          <AssistantPanel
            whyImportant="Wysyłamy maile tylko gdy są ważne — nowe zapytania, podsumowania, przypomnienia. Kontrolujesz co i kiedy dostajesz."
            whatNow="Dostosuj powiadomienia do swojego stylu pracy — włącz co potrzebujesz, ustaw godziny ciszy."
          />
        </div>
      </div>
    </PanelLayout>
  );
}
